// Planning a season: how long it runs, and whether the crew has enough
// approved questions to fill it.
//
// Pure on purpose. The host wizard shows these numbers while a host is still
// deciding, so they have to be right before anything is saved.

import { activeDates } from "./scheduling";
import { localToUtcIso } from "./timezones";

export interface SeasonLength {
  key: string;
  label: string;
  days: number | null; // null = the host picks the dates
}

export const SEASON_LENGTHS: SeasonLength[] = [
  { key: "2w", label: "2 weeks", days: 14 },
  { key: "1m", label: "1 month", days: 30 },
  { key: "6w", label: "6 weeks", days: 42 },
  { key: "custom", label: "Custom", days: null },
];

function pad2(n: number): string { return n < 10 ? `0${n}` : String(n); }

// End date for a preset, counted inclusively: a 14 day season that starts on
// the 1st ends on the 14th, not the 15th.
export function endsOnFor(startsOn: string, days: number): string {
  const [y, m, d] = startsOn.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + (days - 1));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

// Which preset a date range matches, so reopening the wizard shows the chip the
// host actually picked rather than defaulting to Custom.
export function lengthKeyFor(startsOn: string, endsOn: string): string {
  for (const l of SEASON_LENGTHS) {
    if (l.days != null && endsOnFor(startsOn, l.days) === endsOn) return l.key;
  }
  return "custom";
}

export interface SeasonShape {
  startsOn: string;
  endsOn: string;
  activeWeekdays: number[];
  dropsPerDay: number;
  questionsPerDrop: number;
}

// How many approved questions this schedule will consume.
export function questionsNeeded(s: SeasonShape): number {
  if (!s.startsOn || !s.endsOn) return 0;
  const days = activeDates({
    startsOn: s.startsOn,
    endsOn: s.endsOn,
    activeWeekdays: s.activeWeekdays,
    windowStartLocal: "00:00",
    windowEndLocal: "00:00",
    timezone: "UTC",
  }).length;
  return days * Math.max(1, s.dropsPerDay) * Math.max(1, s.questionsPerDrop);
}

export type InventoryVerdict = "good" | "tight" | "short" | "empty";

export interface InventoryReadout {
  needed: number;
  approved: number;
  verdict: InventoryVerdict;
  headline: string;
  detail: string;
}

// Whether the crew can actually feed the season the host has drawn up, said
// plainly enough to act on.
//
// A drop with nothing approved is the failure this exists to prevent: it fires,
// finds no inventory, and the crew gets a silent nothing. So being short is
// reported before the season starts, while there is still time to fix it by
// asking people to check in or inviting more of them.
export function readInventory(needed: number, approved: number): InventoryReadout {
  const base = { needed, approved };
  if (approved === 0) {
    return {
      ...base, verdict: "empty",
      headline: "No questions yet",
      detail: "Nobody has approved any questions. Drops need those to run, so get everyone to check in first.",
    };
  }
  if (approved >= needed) {
    return {
      ...base, verdict: "good",
      headline: "You're good",
      detail: `About ${needed} needed for this schedule, and the crew has ${approved} ready.`,
    };
  }
  // Within a fifth of the target is close enough that a couple of check-ins
  // closes it, which is a different conversation from being genuinely short.
  if (approved >= needed * 0.8) {
    return {
      ...base, verdict: "tight",
      headline: "Cutting it close",
      detail: `About ${needed} needed, and the crew has ${approved}. A couple more check-ins covers it.`,
    };
  }
  return {
    ...base, verdict: "short",
    headline: "You'll run short",
    detail: `About ${needed} needed for this schedule, and the crew has ${approved}. Ask people to check in, add more people, or run fewer drops.`,
  };
}

// A date a person can read. Noon UTC keeps the day from sliding either way when
// a plain YYYY-MM-DD is formatted in a browser west of Greenwich.
export function readableDate(dateStr: string): string {
  if (!dateStr) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
    }).format(new Date(`${dateStr}T12:00:00Z`));
  } catch {
    return dateStr;
  }
}

// Is this start date already behind us? A host reopening a draft they set up
// last week should be told, not left to wonder why nothing fires.
export function isPast(dateStr: string, todayStr: string): boolean {
  return !!dateStr && !!todayStr && dateStr < todayStr;
}

// When the crew can first expect something, as a real instant.
//
// This is the moment the host's own drop window OPENS on the first active day,
// never the secret moment inside it. Secret drop times do not leave the server,
// the host included, so counting down to one would be a leak. The window is
// something the host chose and already knows, so counting down to that is not.
export function firstDropWindowOpens(
  s: { startsOn: string; endsOn: string; activeWeekdays: number[]; windowStart: string; timezone: string }
): string | null {
  if (!s.startsOn || !s.endsOn || s.activeWeekdays.length === 0) return null;
  const days = activeDates({
    startsOn: s.startsOn, endsOn: s.endsOn, activeWeekdays: s.activeWeekdays,
    windowStartLocal: "00:00", windowEndLocal: "00:00", timezone: s.timezone,
  });
  if (days.length === 0) return null;
  return localToUtcIso(days[0].date, s.windowStart || "09:00", s.timezone);
}

// "1 day 18 hours" — how long until something, in the two units that matter.
//
// Two units, never three: "1 day 18 hours 42 minutes" is a stopwatch, and
// nobody reads a season start with that precision. The smaller unit is dropped
// once it stops mattering, so a week out reads "6 days" rather than
// "6 days 0 hours".
export function countdownText(targetMs: number, nowMs: number): string {
  const ms = targetMs - nowMs;
  if (ms <= 0) return "any moment now";

  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  const minutes = totalMinutes - days * 60 * 24 - hours * 60;

  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
  if (days > 0) return hours > 0 ? `${unit(days, "day")} ${unit(hours, "hour")}` : unit(days, "day");
  if (hours > 0) return minutes > 0 ? `${unit(hours, "hour")} ${unit(minutes, "minute")}` : unit(hours, "hour");
  return unit(Math.max(1, minutes), "minute");
}

// The date, spelled out with its year, for the one place a host is committing to
// a season rather than glancing at a row.
export function readableDateLong(dateStr: string): string {
  if (!dateStr) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
    }).format(new Date(`${dateStr}T12:00:00Z`));
  } catch {
    return dateStr;
  }
}

// Where a brand new crew starts, before the host changes anything.
//
// Season 0 defaulted to a random moment anywhere between 9am and 9pm. That is
// technically a surprise and practically a problem: a drop can land while
// somebody is asleep, at work, or driving, and the play window burns down while
// they have no idea it started. Crew feedback liked a lunchtime drop, which is
// the one part of the day most people can actually look at a phone.
//
// A two hour window keeps the surprise (nobody knows the minute) while making it
// catchable (everybody knows roughly when). The host can widen it to the old
// spread in one step if they want.
export const DEFAULT_DROP_WINDOW = { start: "12:00", end: "14:00" };

// A sensible starting schedule for a crew that has picked nothing yet.
export const DEFAULT_DROP_DAYS = [1, 3]; // Monday and Wednesday
